import {
  DsNotificationEventsQueryResult,
  DsNotificationEventsQuery,
  useDsNotificationEventsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import { useProjectContext } from 'context/ProjectContext'

type Query = DsNotificationEventsQuery
export type DSNotification = Query['dsNotifications']['records'][0]

type DSNotificationsProviderContext = Pick<
  DsNotificationEventsQueryResult,
  'refetch'
> & {
  data: {
    dsNotifications: Query['dsNotifications']['records']
    dsDisciplines: Query['dsDisciplines']['records']
  }
  fetching: DsNotificationEventsQueryResult['loading']
  recipients: UseRecipientsFn<DSNotification['recipients']>
}

const { Provider, useContextFn } =
  createStrictContext<DSNotificationsProviderContext>('DSNotificationsContext')

export const useDSNotificationsContext = useContextFn

const DSNotificationsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = useDsNotificationEventsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const recipients = useRecipientsFn({
    data,
    getter: (id, { dsNotifications }) =>
      dsNotifications.records.find(
        ({ notificationEventId }) => notificationEventId === id
      )?.recipients,
    fallback: {
      __typename: 'DSNotificationRecipients',
      every: null,
      byDSDiscipline: [],
    },
  })
  return (
    <Provider
      value={{
        data: {
          dsNotifications: data?.dsNotifications.records ?? [],
          dsDisciplines: data?.dsDisciplines.records ?? [],
        },
        fetching,
        refetch,
        recipients,
      }}
    >
      {children}
    </Provider>
  )
}

export default DSNotificationsProvider
